import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Container } from '@mui/material';
import { Home, ArrowBack, Search } from '@mui/icons-material';

import SEO from '../components/SEO';
import ROUTES from '../config/routes';
import { seoData } from '../data/seoData';

const NotFoundPage = () => {
  const navigate = useNavigate();

  // Track 404 errors for analytics (if you have analytics setup)
  useEffect(() => {
    // You can add analytics tracking here if needed
    // e.g., gtag('event', 'page_view', { page_path: '/404' });
  }, []);

  const handleGoHome = () => {
    navigate(ROUTES.HOME);
  };

  const handleGoBack = () => {
    window.history.back();
  };

  return (
    <>
      <SEO {...seoData.notFound} />

      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#ffffff',
          py: 4
        }}
      >
        <Container maxWidth='md'>
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
              px: 4,
              backgroundColor: 'white',
              borderRadius: 4,
              boxShadow: '0 8px 32px rgba(25, 118, 210, 0.08)',
              border: '1px solid rgba(25, 118, 210, 0.1)'
            }}
          >
            {/* 404 Number */}
            <Typography
              variant='h1'
              sx={{
                fontSize: { xs: '6rem', md: '8rem' },
                fontWeight: 700,
                color: '#1976d2',
                mb: 2,
                letterSpacing: '-0.02em',
                fontFamily: 'Nunito Sans, sans-serif'
              }}
            >
              404
            </Typography>

            {/* Search Icon */}
            <Box sx={{ mb: 3 }}>
              <Search
                sx={{
                  fontSize: { xs: 48, md: 64 },
                  color: '#1976d2',
                  opacity: 0.3
                }}
              />
            </Box>

            {/* Main Message */}
            <Typography
              variant='h4'
              sx={{
                mb: 2,
                fontWeight: 600,
                color: '#212121',
                fontSize: { xs: '1.5rem', md: '2rem' },
                fontFamily: 'Nunito Sans, sans-serif'
              }}
            >
              Page Not Found
            </Typography>

            <Typography
              variant='body1'
              sx={{
                mb: 4,
                color: '#666666',
                fontSize: { xs: '1rem', md: '1.1rem' },
                lineHeight: 1.6,
                maxWidth: '600px',
                mx: 'auto'
              }}
            >
              The page you're looking for doesn't exist. It might have been
              moved, deleted, or you entered the wrong URL.
            </Typography>

            {/* Action Buttons */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                justifyContent: 'center',
                alignItems: 'center',
                mb: 4
              }}
            >
              <Button
                variant='contained'
                size='large'
                startIcon={<Home />}
                onClick={handleGoHome}
                sx={{
                  px: 4,
                  py: 1.5,
                  backgroundColor: '#1976d2',
                  '&:hover': {
                    backgroundColor: '#1565c0',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(25, 118, 210, 0.3)'
                  },
                  transition: 'all 0.3s ease',
                  fontWeight: 600,
                  fontSize: '1rem',
                  borderRadius: 2,
                  textTransform: 'none'
                }}
              >
                Go to Homepage
              </Button>

              <Button
                variant='outlined'
                size='large'
                startIcon={<ArrowBack />}
                onClick={handleGoBack}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderColor: '#1976d2',
                  color: '#1976d2',
                  '&:hover': {
                    borderColor: '#1565c0',
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.3s ease',
                  fontWeight: 600,
                  fontSize: '1rem',
                  borderRadius: 2,
                  textTransform: 'none'
                }}
              >
                Go Back
              </Button>
            </Box>

            {/* Helpful Links */}
            <Box sx={{ borderTop: '1px solid rgba(0, 0, 0, 0.08)', pt: 4 }}>
              <Typography
                variant='body2'
                sx={{
                  color: '#666666',
                  mb: 2,
                  fontWeight: 500
                }}
              >
                You might be looking for:
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  justifyContent: 'center'
                }}
              >
                <Link
                  to={ROUTES.ABOUT_US.ROOT}
                  style={{
                    textDecoration: 'none',
                    color: '#1976d2',
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    padding: '8px 16px',
                    border: '1px solid rgba(25, 118, 210, 0.2)',
                    borderRadius: '20px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={e => {
                    e.target.style.backgroundColor = 'rgba(25, 118, 210, 0.08)';
                    e.target.style.borderColor = '#1976d2';
                  }}
                  onMouseLeave={e => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.borderColor = 'rgba(25, 118, 210, 0.2)';
                  }}
                >
                  Who We Are
                </Link>

                <Link
                  to={ROUTES.STRATEGIES.ROOT}
                  style={{
                    textDecoration: 'none',
                    color: '#1976d2',
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    padding: '8px 16px',
                    border: '1px solid rgba(25, 118, 210, 0.2)',
                    borderRadius: '20px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={e => {
                    e.target.style.backgroundColor = 'rgba(25, 118, 210, 0.08)';
                    e.target.style.borderColor = '#1976d2';
                  }}
                  onMouseLeave={e => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.borderColor = 'rgba(25, 118, 210, 0.2)';
                  }}
                >
                  Strategies
                </Link>

                <Link
                  to={ROUTES.HOW_TO_JOIN.ROOT}
                  style={{
                    textDecoration: 'none',
                    color: '#1976d2',
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    padding: '8px 16px',
                    border: '1px solid rgba(25, 118, 210, 0.2)',
                    borderRadius: '20px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={e => {
                    e.target.style.backgroundColor = 'rgba(25, 118, 210, 0.08)';
                    e.target.style.borderColor = '#1976d2';
                  }}
                  onMouseLeave={e => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.borderColor = 'rgba(25, 118, 210, 0.2)';
                  }}
                >
                  How to Join
                </Link>

                <Link
                  to={ROUTES.CONTACT}
                  style={{
                    textDecoration: 'none',
                    color: '#1976d2',
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    padding: '8px 16px',
                    border: '1px solid rgba(25, 118, 210, 0.2)',
                    borderRadius: '20px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={e => {
                    e.target.style.backgroundColor = 'rgba(25, 118, 210, 0.08)';
                    e.target.style.borderColor = '#1976d2';
                  }}
                  onMouseLeave={e => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.borderColor = 'rgba(25, 118, 210, 0.2)';
                  }}
                >
                  Contact Us
                </Link>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default NotFoundPage;
